<template>
  <div>
    <Header />
  <v-container fluid>
    <v-card v-if="loading" class="pa-6">
      <v-skeleton-loader type="article, actions"></v-skeleton-loader>
    </v-card>

    <v-card v-else-if="orderData">
      <!-- Header -->
      <v-card-title>
        <v-btn
          icon
          @click="$router.back()"
          class="mr-3"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h2>Bestelldetails - {{ orderId }}</h2>
          <div class="caption">
            Erstellt: {{ formatDate(orderData.order.createDate) }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <div class="text-right">
          <v-chip
            :color="orderData.order.state === 'SHIPPED' ? 'success' : 'error'"
            class="mb-1"
          >
            {{ orderData.order.state }}
          </v-chip>
          <div v-if="orderData.order.tracking" class="caption">
            <v-icon small>mdi-package-variant</v-icon>
            Tracking: {{ orderData.order.tracking === true ? 'Aktiv' : orderData.order.tracking }}
          </div>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Two Column Layout -->
      <v-row class="pa-4">
        <!-- Left Column - Original Data -->
        <v-col cols="12" md="6">
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1">
              <v-icon left>mdi-package-variant</v-icon>
              Originale Bestelldaten
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <!-- Customer Information -->
              <div class="mb-4">
                <h4 class="mb-2">Kundeninformationen</h4>
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="font-weight-medium">Name:</td>
                      <td>{{ originalAddress.fullName || originalAddress.name }}</td>
                    </tr>
                    <tr v-if="originalAddress.company">
                      <td class="font-weight-medium">Firma:</td>
                      <td>{{ originalAddress.company }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Straße:</td>
                      <td>{{ originalAddress.street }} {{ originalAddress.houseNumber }}</td>
                    </tr>
                    <tr v-if="originalAddress.addressAddition">
                      <td class="font-weight-medium">Adresszusatz:</td>
                      <td>{{ originalAddress.addressAddition }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">PLZ/Ort:</td>
                      <td>{{ originalAddress.zip }} {{ originalAddress.city }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Land:</td>
                      <td>{{ originalAddress.country }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <!-- Order Information -->
              <div class="mb-4">
                <h4 class="mb-2">Bestellinformationen</h4>
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="font-weight-medium">Bestelldatum:</td>
                      <td>{{ formatDate(orderData.order.createDate) }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Versandart:</td>
                      <td>
                        <v-chip
                          :color="orderData.order.shipping?.type === 'PRIME' ? 'primary' : 'default'"
                          small
                        >
                          {{ orderData.order.shipping?.type || 'STANDARD' }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">Gesamtgewicht:</td>
                      <td>{{ calculateTotalWeight() }}g</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <!-- Products -->
              <div>
                <h4 class="mb-2">Produkte</h4>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Menge</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="product in orderData.order.products" :key="product.sku">
                      <td>{{ product.sku }}</td>
                      <td>{{ product.name }}</td>
                      <td>{{ product.quantity }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-card-text>
          </v-card>

          <!-- Label Information (if exists) -->
          <v-card outlined class="mb-4" v-if="orderData.order.labelUrls && orderData.order.labelUrls.length > 0">
            <v-card-title class="subtitle-1">
              <v-icon left :color="hasValidLabel ? 'success' : 'warning'">mdi-label</v-icon>
              Erstellte Labels
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-list dense>
                <v-list-item
                  v-for="(url, index) in orderData.order.labelUrls"
                  :key="index"
                  :href="url"
                  target="_blank"
                  link
                >
                  <v-list-item-icon>
                    <v-icon color="primary">mdi-file-pdf-box</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      Label {{ index + 1 }}
                      <v-chip 
                        v-if="url.includes('FALLBACK_LABEL')" 
                        x-small 
                        color="warning"
                        class="ml-2"
                      >
                        Fallback
                      </v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-truncate" style="max-width: 400px;">
                      {{ getFileName(url) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon small :href="url" target="_blank">
                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              
              <!-- Tracking Information -->
              <div v-if="hasTrackingInfo" class="mt-3">
                <h4 class="mb-2">Tracking-Informationen</h4>
                <v-simple-table dense>
                  <tbody>
                    <tr v-for="(shipment, index) in billbeeShipments" :key="index">
                      <td class="font-weight-medium">{{ shipment.Shipper }}:</td>
                      <td>
                        <a :href="shipment.TrackingUrl" target="_blank" class="text-decoration-none">
                          {{ shipment.ShippingId }}
                          <v-icon small class="ml-1">mdi-open-in-new</v-icon>
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-card-text>
          </v-card>

          <!-- Error Analysis -->
          <v-card outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="error">mdi-alert-circle</v-icon>
              Fehleranalyse & Ereignisprotokoll
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-alert
                v-for="(error, index) in orderData.errorAnalysis.specificErrors"
                :key="index"
                type="error"
                dense
                class="mb-2"
              >
                {{ error }}
              </v-alert>

              <!-- Suggested Fixes -->
              <div v-if="orderData.suggestedFixes.length > 0" class="mt-4">
                <h4 class="mb-2">Vorgeschlagene Lösungen</h4>
                <v-list dense>
                  <v-list-item
                    v-for="(fix, index) in orderData.suggestedFixes"
                    :key="index"
                  >
                    <v-list-item-icon>
                      <v-icon color="warning">mdi-lightbulb</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ fix.description }}</v-list-item-title>
                      <v-list-item-subtitle v-if="fix.suggestion">
                        {{ fix.suggestion }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>

              <!-- Event Log -->
              <div class="mt-4">
                <h4 class="mb-2">Letzte Ereignisse</h4>
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Ereignisprotokoll anzeigen ({{ recentEvents.length }} Ereignisse)
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-timeline dense>
                        <v-timeline-item
                          v-for="(event, index) in recentEvents"
                          :key="index"
                          :color="event.error ? 'error' : 'success'"
                          small
                        >
                          <template v-slot:opposite>
                            <span class="caption">{{ formatDate(event.timestamp) }}</span>
                          </template>
                          <v-card :color="event.error ? 'error' : ''" :dark="event.error">
                            <v-card-text class="pa-2">
                              <div class="caption">{{ event.event }}</div>
                              <div class="caption font-weight-light" v-if="event.tag">
                                Method: {{ event.tag }}
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-timeline-item>
                      </v-timeline>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Right Column - Retry Options -->
        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="primary">mdi-cog</v-icon>
              Label-Erstellung wiederholen
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-form ref="retryForm" v-model="formValid">
                <!-- Address Override -->
                <div class="mb-4">
                  <h4 class="mb-2">
                    Adresse überschreiben
                    <v-switch
                      v-model="enableAddressOverride"
                      class="mt-0 ml-2"
                      dense
                      hide-details
                      style="display: inline-block; vertical-align: middle;"
                    ></v-switch>
                  </h4>
                  
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="addressOverride.firstName"
                        label="Vorname"
                        :disabled="!enableAddressOverride"
                        dense
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        v-model="addressOverride.lastName"
                        label="Nachname"
                        :disabled="!enableAddressOverride"
                        dense
                        outlined
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  
                  <v-text-field
                    v-model="addressOverride.company"
                    label="Firma (optional)"
                    :disabled="!enableAddressOverride"
                    dense
                    outlined
                  ></v-text-field>
                  
                  <v-row>
                    <v-col cols="8">
                      <v-text-field
                        v-model="addressOverride.street"
                        label="Straße"
                        :disabled="!enableAddressOverride"
                        dense
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="addressOverride.houseNumber"
                        label="Hausnummer"
                        :disabled="!enableAddressOverride"
                        dense
                        outlined
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  
                  <v-text-field
                    v-model="addressOverride.addressAddition"
                    label="Adresszusatz (optional)"
                    :disabled="!enableAddressOverride"
                    dense
                    outlined
                  ></v-text-field>
                  
                  <v-row>
                    <v-col cols="4">
                      <v-text-field
                        v-model="addressOverride.zip"
                        label="PLZ"
                        :disabled="!enableAddressOverride"
                        dense
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="8">
                      <v-text-field
                        v-model="addressOverride.city"
                        label="Ort"
                        :disabled="!enableAddressOverride"
                        dense
                        outlined
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  
                  <v-text-field
                    v-model="addressOverride.country"
                    label="Land"
                    :disabled="!enableAddressOverride"
                    dense
                    outlined
                  ></v-text-field>
                </div>

                <!-- Shipping Profile Override -->
                <!--<div class="mb-4">
                  <h4 class="mb-2">Shipping Profile</h4>
                  <v-select
                    v-model="selectedProfileId"
                    :items="shippingProfiles"
                    item-text="displayName"
                    item-value="id"
                    label="Select Shipping Profile"
                    outlined
                    dense
                    clearable
                    :loading="loadingProfiles"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ item.provider }} - Max: {{ item.maxWeight }}g
                          <v-chip x-small class="ml-1" v-if="item.under3cm">Under 3cm</v-chip>
                          <v-chip x-small class="ml-1" v-if="item.tracking">Tracking</v-chip>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </div>-->

                <!-- Package Size -->
                <div class="mb-4">
                  <h4 class="mb-2">Paketgröße</h4>
                  <v-radio-group
                    v-model="packageUnder3cm"
                    row
                  >
                    <v-radio
                      label="Automatisch erkennen"
                      :value="null"
                    ></v-radio>
                    <v-radio
                      label="Unter 8cm"
                      :value="true"
                    ></v-radio>
                    <v-radio
                      label="Über 8cm"
                      :value="false"
                    ></v-radio>
                  </v-radio-group>
                </div>

                <!-- Current Shipping Profile Info -->
                <v-alert
                  v-if="orderData.currentShippingProfile"
                  type="info"
                  dense
                  class="mb-4"
                >
                  <strong>Zuletzt verwendetes Profil:</strong> {{ orderData.currentShippingProfile.billbee?.name || 'Unknown' }}
                </v-alert>

                <!-- Action Buttons -->
                <v-card-actions class="px-0">
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    @click="resetForm"
                  >
                    Zurücksetzen
                  </v-btn>
                  <v-btn
                    color="primary"
                    @click="retryLabelCreation"
                    :loading="retrying"
                    :disabled="!formValid"
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Label-Erstellung wiederholen
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card-text>
          </v-card>

          <!-- Success Message -->
          <v-alert
            v-if="retrySuccess"
            type="success"
            class="mt-4"
            dismissible
            @input="retrySuccess = false"
          >
            <h4>Label erfolgreich erstellt!</h4>
            <p class="mb-0">Das Label wurde erstellt und der Bestellstatus wurde auf VERSENDET aktualisiert.</p>
            <v-btn
              text
              color="white"
              @click="$router.push('/failed-labels')"
              class="mt-2"
            >
              Zurück zur Liste
            </v-btn>
          </v-alert>
        </v-col>
      </v-row>
    </v-card>

    <!-- Error State -->
    <v-card v-else class="pa-6 text-center">
      <v-icon size="64" color="error">mdi-alert-circle</v-icon>
      <h3 class="mt-4">Bestellung nicht gefunden</h3>
      <v-btn
        color="primary"
        @click="$router.push('/failed-labels')"
        class="mt-4"
      >
        Zurück zur Liste
      </v-btn>
    </v-card>
  </v-container>
  </div>
</template>

<script>
import Header from './header.vue';
import FailedLabelsService from '@/services/FailedLabelsService';

export default {
  name: 'FailedLabelDetails',
  components: {
    Header
  },
  data() {
    return {
      orderId: null,
      orderData: null,
      loading: false,
      retrying: false,
      retrySuccess: false,
      formValid: true,
      enableAddressOverride: false,
      addressOverride: {
        firstName: '',
        lastName: '',
        company: '',
        street: '',
        houseNumber: '',
        addressAddition: '',
        zip: '',
        city: '',
        country: 'DE'
      },
      selectedProfileId: null,
      packageUnder3cm: null,
      shippingProfiles: [],
      loadingProfiles: false
    };
  },
  computed: {
    originalAddress() {
      // Use the formatted Billbee address from backend if available
     
      
      // Fallback to order data
      if (!this.orderData?.order) return {};
      const customer = this.orderData.order.billbeeOrder.ShippingAddress || {};
      
      return {
        firstName: customer.FirstName || '',
        lastName: customer.LastName || '',
        street: customer.Street || '', // Note: typo in schema
        houseNumber: customer.HouseNumber || '',
        addressAddition: customer.NameAddition || '',
        zip: customer.Zip || '',
        city: customer.City || '',
        country: customer.Country || 'DE',  
        company: customer.Company || '',
        fullName: `${customer.FirstName || ''} ${customer.LastName || ''}`.trim()
      };
    },
    
    recentEvents() {
      if (!this.orderData?.order?.events) return [];
      return this.orderData.order.events
        .slice()
        .reverse();
    },
    
    hasValidLabel() {
      return this.orderData?.order?.labelUrls?.some(url => !url.includes('FALLBACK_LABEL'));
    },
    
    hasTrackingInfo() {
      return this.billbeeShipments.length > 0;
    },
    
    billbeeShipments() {
      return this.orderData?.billbeeOrder?.ShippingIds || [];
    }
  },
  mounted() {
    this.orderId = this.$route.params.orderId;
    this.loadOrderDetails();
    this.loadShippingProfiles();
  },
  methods: {
    async loadOrderDetails() {
      this.loading = true;
      try {
        const response = await FailedLabelsService.getFailedLabelDetails(this.orderId);
        
        if (response.data.success) {
          this.orderData = response.data.data;
          this.populateAddressFields();
        }
      } catch (error) {
        console.error('Error loading order details:', error);
        this.$root.$emit('showError', 'Fehler beim Laden der Bestelldetails');
      } finally {
        this.loading = false;
      }
    },

    async loadShippingProfiles() {
      this.loadingProfiles = true;
      try {
        const response = await FailedLabelsService.getShippingProfiles();
        
        if (response.data.success) {
          this.shippingProfiles = response.data.data.map(profile => ({
            ...profile,
            displayName: `${profile.name} (${profile.provider})`
          }));
        }
      } catch (error) {
        console.error('Error loading shipping profiles:', error);
      } finally {
        this.loadingProfiles = false;
      }
    },

    populateAddressFields() {
      const addr = this.originalAddress;

      console.log(addr);
      this.addressOverride = {
        firstName: addr.firstName || '',
        lastName: addr.lastName || '',
        company: addr.company || '',
        street: addr.street || '',
        houseNumber: addr.houseNumber || '',
        addressAddition: addr.addressAddition || '',
        zip: addr.zip || '',
        city: addr.city || '',
        country: addr.country || 'DE'
      };
    },

    calculateTotalWeight() {
      if (!this.orderData?.billbeeOrder?.ShipWeightKg) {
        // Try to calculate from order items
        if (this.orderData?.billbeeOrder?.OrderItems) {
          const totalWeight = this.orderData.billbeeOrder.OrderItems.reduce((sum, item) => {
            const weight = item.Product?.Weight || 0;
            return sum + (weight * item.Quantity);
          }, 0);
          
          if (totalWeight > 0) {
            // Convert from grams to display format
            if (totalWeight >= 1000) {
              return `${(totalWeight / 1000).toFixed(2)} kg`;
            }
            return `${totalWeight} g`;
          }
        }
        return 'N/A';
      }
      
      // ShipWeightKg is in kg, convert to appropriate display format
      const weightKg = parseFloat(this.orderData.billbeeOrder.ShipWeightKg);
      if (weightKg >= 1) {
        return `${weightKg.toFixed(2)} kg`;
      } else {
        return `${(weightKg * 1000).toFixed(0)} g`;
      }
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getFileName(url) {
      if (!url) return '';
      const parts = url.split('/');
      const filename = parts[parts.length - 1];
      // Decode URL encoded characters
      return decodeURIComponent(filename);
    },

    resetForm() {
      this.enableAddressOverride = false;
      this.selectedProfileId = null;
      this.packageUnder3cm = null;
      this.populateAddressFields();
    },

    async retryLabelCreation() {
      this.retrying = true;
      this.retrySuccess = false;
      
      try {
        const payload = {};
        
        // Only include address override if enabled
        if (this.enableAddressOverride) {
          payload.addressOverride = this.addressOverride;
        }
        
        // Include profile if selected
        if (this.selectedProfileId) {
          payload.shippingProfileId = this.selectedProfileId;
        }
        
        // Include package size if specified
        if (this.packageUnder3cm !== null) {
          payload.under3cm = this.packageUnder3cm;
        }
        
        const response = await FailedLabelsService.retryLabelCreation(this.orderId, payload);
        
        if (response.data.success) {
          this.retrySuccess = true;
          this.$root.$emit('showSuccess', 'Label erfolgreich erstellt');
          // Reload order data to show updated status
          await this.loadOrderDetails();
        } else {
          throw new Error(response.data.message || 'Retry failed');
        }
      } catch (error) {
        console.error('Error retrying label:', error);
        this.$root.$emit('showError', 
          error.response?.data?.message || 'Fehler beim erneuten Versuch der Label-Erstellung'
        );
      } finally {
        this.retrying = false;
      }
    }
  }
};
</script>

<style scoped>
.v-skeleton-loader {
  max-width: none;
}
</style>